import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import basic from './common/basic.js';
import updater from '../updater.js';
import { has, addFileSufix, getFileExtension } from '../../../utils.js';
/* State example:
state: {
  byId: {
    "asset_0": {
      "id": "asset_0",
      "name": "Root",
      "children": ["asset_1],
      "root": true,
    },
    "asset_1": {
      "id": "asset_1",
      "name": "Start folder",
      "children": ["asset_2"]
    },
    "asset_2": {
      "id": "asset_2",
      "type": "image",
      "name": "Death Star",
      "file": "1879xvpsqz1qojpg.jpg",
      "size": "1.08 MB"
    },
    "asset_3": {
      "id": "asset_3",
      "type": "template-image",
      "name": "Painter Room",
      "file": ""b73aa11a-1b40-4636-a71d-adb21e616e11"",
    }
  }
}
*/

const options = {};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),

  /**
   * Check if an asset reference is an image
   * @param   {String}  id   The asset id to check
   * @return  {Boolean}
   */
  getIsImage(state, getters) {
    return id => {
      const asset = state.byId[id];
      if (!asset) return false;
      return asset.type === 'image';
    };
  },

  /**
   * Convert an image file name to a full url
   * @param   {string} file   The image file name
   * @returns {string}
   */
  getImageUrl(state, getters, rootState, rootGetters) {
    return file => {
      const assetsUrl = rootGetters['settings/getAssetsUrl'];
      const hash = rootGetters['user/projects/getOpen'];
      return `${assetsUrl}/projects/${hash}/images/${file}`;
    };
  },

  /**
   * Convert a template image file name to a full url
   * @param   {string} file   The template image file name
   * @returns {string}
   */
  getTemplateImageUrl(state) {
    return file => {
      return `/assets/getTemplateImage/${file}`;
    };
  },

  /**
   * Convert an image (project cover) to a full url
   * @param   {string}  file       The cover file name in the project folder
   * @param   {string}  hash       The project hash
   * @return  {string}
   */
  getProjectCoverUrl(state, getters, rootState, rootGetters) {
    return (file, hash) => {
      // Check if cover is enabled
      if (file) {
        const assetsUrl = rootGetters['settings/getAssetsUrl'];
        return `${assetsUrl}/projects/${hash}/images/${file}`;
      }
      return null;
    };
  },

  /**
   * Return the audio url (if the asset is an audio);
   * @param {string} id
   * @returns {string|null}
   */
  getAudioUrl(state, getters, rootState, rootGetters) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { file, type } = state.byId[id];
      const assetsUrl = rootGetters['settings/getAssetsUrl'];
      const hash = rootGetters['user/projects/getOpen'];
      if (type === 'audio')
        return `${assetsUrl}/projects/${hash}/audios/${file}`;
      if (type === 'template-audio') return `/assets/getTemplateAudio/${file}`;
      return null;
    };
  },

  /**
   * Returns the only the assets of type 'audio'
   * @returns {Array}   The audio assets
   */
  getAudioAssets(state, getters, rootState, rootGetters) {
    return Object.values(state.byId).filter(
      asset => asset.type === 'audio' || asset.type === 'template-audio'
    );
  },

  /**
   * Return the full image url (if the asset is an image or template-image)
   * @param   {String}      id    The asset id
   * @return  {String|null}
   */
  getFullImageUrl(state, getters) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { file, type } = state.byId[id];
      if (!file) return null;
      if (type === 'image') return getters.getImageUrl(file);
      if (type === 'template-image') return getters.getTemplateImageUrl(file);

      return null;
    };
  },

  /**
   * Return the medium image (if the asset is an image or template-image)
   * @param   {String}      id    The asset id
   * @returns {String|null}
   */
  getMediumImageUrl(state, getters) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { file, type } = state.byId[id];
      if (!file) return null;
      const mediumFile = addFileSufix(file, '_m');
      if (type === 'image') return getters.getImageUrl(mediumFile);
      if (type === 'template-image')
        return getters.getTemplateImageUrl(mediumFile);

      return null;
    };
  },

  /**
   * Return the small image (if the asset is an image or template-image)
   * @param   {String}      id   The asset id
   * @returns {String|null}
   */
  getSmallImageUrl(state, getters) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { file, type } = state.byId[id];
      if (!file) return null;
      const smallFile = addFileSufix(file, '_s');
      if (type === 'image') return getters.getImageUrl(smallFile);
      if (type === 'template-image')
        return getters.getTemplateImageUrl(smallFile);

      return null;
    };
  },

  /**
   * Return the tiny image (if the asset is an image or template-image)
   * @param   {String}      id   The asset id
   * @returns {String|null}
   */
  getTinyImageUrl(state, getters) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { file, type } = state.byId[id];
      if (!file) return null;
      const tinyFile = addFileSufix(file, '_t');
      if (type === 'image') return getters.getImageUrl(tinyFile);
      if (type === 'template-image')
        return getters.getTemplateImageUrl(tinyFile);

      return null;
    };
  },

  /**
   * Return the file (if the asset exists)
   * @param   {String}        id        The asset id
   * @returns {String|null}
   */
  getFile(state) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { file } = state.byId[id];
      return file;
    };
  },

  /**
   * Return the file extension (if the asset exists)
   * @param   {String}        id     The asset id
   * @returns {String|null}
   */
  getExtension(state) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { file } = state.byId[id];
      if (!file) return null;
      return getFileExtension(file);
    };
  },

  /**
   * Return the name (if the asset exists)
   * @param   {String}        id        The asset id
   * @returns {String|null}
   */
  getName(state) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { name } = state.byId[id];
      return name;
    };
  },

  /**
   * Return the size (if the asset exists)
   * @param   {String}        id        The asset id
   * @returns {String|null}
   */
  getSize(state) {
    return id => {
      if (!has(state.byId, id)) return null;
      const { size } = state.byId[id];
      return size;
    };
  },
};

const actions = {
  ...basic.actions({
    type: 'assets',
    container: { type: 'assets', prop: 'children' },
  }),

  /**
   * Create a new asset
   * @param   {Object}        props                 Any extra or updated properties
   * @param   {String|null}   tId                   The container id (if null uses the root container)
   * @param   {Number}        tIndex                The index in the container
   * @param   {Boolean}       apply                 Whether to apply the patch after finishing
   * @returns {String}                              The id of the asset that was created
   */
  createAsset(
    context,
    { props = {}, tId = null, tIndex = 0, tExpand = false, apply = true }
  ) {
    const id = uuidv4();
    if (tId && tExpand) {
      context.dispatch(
        'project/config/assets/setCollapsed',
        { id: tId, value: false, apply: false },
        { root: true }
      );
    }
    // In case no parent container specified use root container
    tId = tId || context.getters.getRootContainer.id;
    context.dispatch('create', {
      node: {
        id,
        ...props,
      },
      tId,
      tIndex,
      apply: false,
    });
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Remove an asset with specified id from the store
   * @param {String}  id    The id of the asset
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  deleteAsset(context, { id, apply }) {
    // Find all the descendants of this asset
    const ids = _.cloneDeep(
      context.getters.getDescendantNodes(id, 'assets', 'children')
    );
    // Add the current asset also
    ids.push(id);

    // Remove any element references on these assets
    context.dispatch(
      'project/data/elements/removeAssetReferences',
      { ids, apply: false },
      { root: true }
    );
    // Remove any component references on these assets
    context.dispatch(
      'project/data/components/removeAssetReferences',
      { ids, apply: false },
      { root: true }
    );

    // Remove from any attribute asset lists in elements-commponents
    context.dispatch(
      'project/data/attributes/deleteAssets',
      { ids, apply: false },
      { root: true }
    );

    // Remove the descendant assets (not their tree references - these will be deleted by the actual parent delete)
    ids
      .filter(assetId => assetId !== id)
      .forEach(assetId => {
        context.dispatch('delete', {
          id: assetId,
          apply: false,
          deleteReference: false,
        });
      });

    // Remove the asset object (and the references to its childern)
    context.dispatch('delete', { id, apply: false });

    updater.complete({ apply });
  },

  /**
   * Update some of the asset's properties.
   * @param {Object}  context
   * @param {String}  id      The id that should be updated
   * @param {Object}  props   Contains only the properties that are changed
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  updateAsset(context, { id, props, apply }) {
    console.log('Updating asset', id, 'props:', props);
    // Update the asset's properties
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },
};

const mutations = {};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default module;
